"use client";

import { MealType } from "@/types/post";
import { Card, CardBody, Chip, Avatar } from "@nextui-org/react";
import { Leaf, Droplets, MapPin, Tags, ShoppingBag, Flame, Beef, Citrus, Egg, Heart } from "lucide-react";
import { useState, useEffect } from "react";

interface MealContentProps {
  meal: MealType;
  locationName?: string | null;
}

// Helper function to extract nutritional information from content
const extractNutritionFromContent = (content: string, nutrient: string): string => {
  // Try multiple regex patterns to extract the value
  const patterns = [
    new RegExp(`${nutrient}:\\s*([0-9]+)`, 'i'),
    new RegExp(`${nutrient}\\s+([0-9]+)`, 'i'),
    new RegExp(`([0-9]+)\\s*g?\\s+${nutrient}`, 'i')
  ];
  
  for (const regex of patterns) {
    const match = content.match(regex);
    if (match && match[1]) {
      return match[1];
    }
  }
  
  // If no match found, try to find any number near the nutrient name
  const nearbyMatch = content.match(new RegExp(`.{0,20}${nutrient}.{0,20}`, 'i'));
  if (nearbyMatch) {
    const numberMatch = nearbyMatch?.[0]?.match(/\d+/);
    if (numberMatch) {
      return numberMatch[0];
    }
  }
  
  return "0";
};

// Helper function to extract meal data from HTML comments
const extractMealDataFromHTMLComment = (content: string): any => {
  try {
    // Look for the special marker that indicates meal data
    const regex = /<!-- MEAL_DATA: ([\s\S]*?) -->/;
    const match = content.match(regex);
    
    if (!match || !match[1]) {
      return null;
    }
    
    // Parse the JSON string
    const jsonString = match[1];
    const mealData = JSON.parse(jsonString);
    
    console.log("Found embedded meal data with format version:",
      mealData.formatVersion || "legacy");
    
    return mealData;
  } catch (error) {
    console.error("Failed to parse meal data from HTML comment", error);
    return null;
  }
};

export function MealContent({ meal, locationName }: MealContentProps) {
  // Extract embedded meal data if available
  const [extractedData, setExtractedData] = useState<any>(null);
  useEffect(() => {
    // Check if we need to extract data from post content
    if (meal.description && meal?.description?.includes("<!-- MEAL_DATA:")) {
      const data = extractMealDataFromHTMLComment(meal.description);
      if (data) {
        console.log("Successfully extracted meal data from HTML comment");
        console.log("Extracted data structure:", {
          name: data.name,
          calories: data.calories,
          protein: data.protein,
          carbs: typeof data.carbs === 'object'
            ? `Object with total: ${data.carbs?.total}`
            : data.carbs,
          fats: typeof data.fats === 'object'
            ? `Object with total: ${data.fats?.total}`
            : data.fats,
          nutritionalInfo: data.nutritionalInfo,
          formatVersion: data.formatVersion
        });
        setExtractedData(data);
      }
    }
  }, [meal.description]);
  
  // Enhanced logging to verify what's being received
  console.log("MealContent received meal from props:", {
    id: meal.id,
    name: meal.name,
    calories: meal.calories,
    protein: meal.protein,
    carbs: typeof meal.carbs === 'object'
      ? `Object with total: ${(meal.carbs as any)?.total}`
      : meal.carbs,
    fats: typeof meal.fats === 'object'
      ? `Object with total: ${(meal.fats as any)?.total}`
      : meal.fats,
    nutritionalInfo: meal.nutritionalInfo
  });
  console.log("Extracted data from HTML comment:", extractedData ? "Available" : "Not available");
  console.log("Extracted data from HTML comment:", extractedData);
  
  // Debug log for nutritional values - EXACT RAW VALUES
  console.log("EXACT RAW nutritional values:", {
    calories: {
      rawCalories: meal.calories,
      rawNutritionalInfo: meal.nutritionalInfo?.calories,
      fromExtracted: extractedData?.calories,
      fromDescription: meal.description ? extractNutritionFromContent(meal.description, 'calories') : null
    },
    protein: {
      rawProtein: meal.protein,
      rawNutritionalInfo: meal.nutritionalInfo?.protein,
      fromExtracted: extractedData?.protein,
      fromDescription: meal.description ? extractNutritionFromContent(meal.description, 'protein') : null
    }
  });
  
  // Get nutritional values with fallbacks
  const getCalories = () => {
    // Priority 1: Direct property from database
    if (meal.calories) {
      return meal?.calories?.replace(/calories/i, '').replace(/approximately/i, '').trim();
    }
    
    // Priority 2: Extracted data from HTML comment
    if (extractedData?.calories) {
      return extractedData?.calories?.replace(/calories/i, '').replace(/approximately/i, '').trim();
    }
    
    // Priority 3: From nutritionalInfo object
    if (meal.nutritionalInfo?.calories) {
      return meal?.nutritionalInfo?.calories.replace(/calories/i, '').replace(/approximately/i, '').trim();
    }
    
    // Priority 4: Extract from description text
    if (meal.description) {
      const value = extractNutritionFromContent(meal.description, 'calories');
      if (value !== "0") return value;
    }
    
    // Fallback to default values only if all else fails
    return "500";
  };
  
  const getProtein = () => {
    // Priority 1: Direct property from database
    if (meal.protein) {
      return meal?.protein?.replace(/protein/i, '').replace(/approximately/i, '').trim();
    }
    
    // Priority 2: Extracted data from HTML comment
    if (extractedData?.protein) {
      return extractedData?.protein?.replace(/protein/i, '').replace(/approximately/i, '').trim();
    }
    
    // Priority 3: From nutritionalInfo object
    if (meal.nutritionalInfo?.protein) {
      return meal?.nutritionalInfo?.protein.replace(/protein/i, '').replace(/approximately/i, '').trim();
    }
    
    // Priority 4: Extract from description text
    if (meal.description) {
      const value = extractNutritionFromContent(meal.description, 'protein');
      if (value !== "0") return value;
    }
    
    // Fallback to default values only if all else fails
    return "20";
  };
  
  const getCarbs = () => {
    // Priority 1: Direct property from database as object
    if (typeof meal.carbs === 'object' && (meal.carbs as any)?.total) {
      return (meal.carbs as any).total.replace(/carbs/i, '').replace(/approximately/i, '').trim();
    }
    
    // Priority 2: Extracted data from HTML comment
    if (extractedData?.carbs) {
      if (typeof extractedData.carbs === 'object' && extractedData?.carbs?.total) {
        return extractedData?.carbs?.total.replace(/carbs/i, '').replace(/approximately/i, '').trim();
      } else if (typeof extractedData.carbs === 'string') {
        return extractedData?.carbs?.replace(/carbs/i, '').replace(/approximately/i, '').trim();
      }
    }
    
    // Priority 3: From nutritionalInfo object
    if (meal.nutritionalInfo?.carbs) {
      return meal?.nutritionalInfo?.carbs.replace(/carbs/i, '').replace(/approximately/i, '').trim();
    }
    
    // Priority 4: Extract from description text
    if (meal.description) {
      const value = extractNutritionFromContent(meal.description, 'carbs');
      if (value !== "0") return value;
    }
    
    // Fallback to default values only if all else fails
    return "30";
  };
  
  const getFats = () => {
    // Priority 1: Direct property from database as object
    if (typeof meal.fats === 'object' && (meal.fats as any)?.total) {
      return (meal.fats as any).total.replace(/fats/i, '').replace(/approximately/i, '').trim();
    }
    
    // Priority 2: Extracted data from HTML comment
    if (extractedData?.fats) {
      if (typeof extractedData.fats === 'object' && extractedData?.fats?.total) {
        return extractedData?.fats?.total.replace(/fats/i, '').replace(/approximately/i, '').trim();
      } else if (typeof extractedData.fats === 'string') {
        return extractedData?.fats?.replace(/fats/i, '').replace(/approximately/i, '').trim();
      }
    }
    
    // Priority 3: From nutritionalInfo object
    if (meal.nutritionalInfo?.fats) {
      return meal?.nutritionalInfo?.fats.replace(/fats/i, '').replace(/approximately/i, '').trim();
    }
    
    // Priority 4: Extract from description text
    if (meal.description) {
      const value = extractNutritionFromContent(meal.description, 'fats');
      if (value !== "0") return value;
    }
    
    // Fallback to default values only if all else fails
    return "10";
  };
  
  const getQualityScore = () => {
    // Priority 1: Extracted data from HTML comment
    if (extractedData?.qualityScore) {
      const score = extractedData?.qualityScore?.replace(/[^0-9]/g, '');
      if (score) return score;
    }
    
    // Priority 2: From nutritionalInfo object
    if (meal.nutritionalInfo?.qualityScore) {
      const score = meal?.nutritionalInfo?.qualityScore.replace(/[^0-9]/g, '');
      if (score) return score;
    }
    
    // Priority 3: Try to extract from description if it contains a quality score
    if (meal.description && meal?.description?.toLowerCase().includes('score')) {
      const scoreMatch = meal?.description?.match(/score:?\s*(\d+)/i);
      if (scoreMatch && scoreMatch[1]) {
        return scoreMatch[1];
      }
    }
    
    // Default to a reasonable score if nothing found
    return "85";
  };
  
  return (
    <Card className="bg-content1 mt-6 shadow-md">
      <CardBody className="gap-4">
        <div>
          <h3 className="text-xl font-semibold mb-2">{meal.name}</h3>
          <p className="text-default-600">{meal.description}</p>
        </div>

        {/* Nutritional metrics */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          {/* Calories */}
          <Card className="bg-content2 dark:bg-content2 shadow-sm border border-divider">
            <CardBody className="p-2 flex flex-col items-center">
              <Flame className="h-5 w-5 text-orange-500 dark:text-orange-400" />
              <div className="text-sm font-bold">{getCalories() || "—"}</div>
              <div className="text-xs text-default-500">Calories</div>
            </CardBody>
          </Card>
          
          {/* Protein */}
          <Card className="bg-content2 dark:bg-content2 shadow-sm border border-divider">
            <CardBody className="p-2 flex flex-col items-center">
              <Beef className="h-5 w-5 text-red-500 dark:text-red-400" />
              <div className="text-sm font-bold">{getProtein() || "—"}</div>
              <div className="text-xs text-default-500">Protein</div>
            </CardBody>
          </Card>
          
          {/* Carbs */}
          <Card className="bg-content2 dark:bg-content2 shadow-sm border border-divider">
            <CardBody className="p-2 flex flex-col items-center">
              <Citrus className="h-5 w-5 text-blue-500 dark:text-blue-400" />
              <div className="text-sm font-bold">{getCarbs() || "—"}</div>
              <div className="text-xs text-default-500">Carbs</div>
            </CardBody>
          </Card>
          
          {/* Fats */}
          <Card className="bg-content2 dark:bg-content2 shadow-sm border border-divider">
            <CardBody className="p-2 flex flex-col items-center">
              <Egg className="h-5 w-5 text-yellow-500 dark:text-yellow-400" />
              <div className="text-sm font-bold">{getFats() || "—"}</div>
              <div className="text-xs text-default-500">Fats</div>
            </CardBody>
          </Card>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {/* Quality Score */}
          {meal.nutritionalInfo?.qualityScore && (
            <Chip
              startContent={<Heart className="w-4 h-4 text-pink-500 dark:text-pink-400" />}
              color="primary"
              variant="flat"
              classNames={{
                base: "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300",
                content: "font-medium"
              }}
            >
              Score: {getQualityScore()}/100
            </Chip>
          )}
          
          {/* Organic */}
          {meal.isOrganic && (
            <Chip
              startContent={<Leaf className="w-4 h-4 text-green-500 dark:text-green-400" />}
              color="success"
              variant="flat"
              classNames={{
                base: "bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-300",
                content: "font-medium"
              }}
            >
              Organic
            </Chip>
          )}
          
          {/* Seed Oil Free */}
          {!meal.hasSeedOils && (
            <Chip
              startContent={<Droplets className="w-4 h-4 text-green-500 dark:text-green-400" />}
              color="success"
              variant="flat"
              classNames={{
                base: "bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-300",
                content: "font-medium"
              }}
            >
              Seed Oil Free
            </Chip>
          )}
          
          {/* Contains Seed Oils */}
          {meal.hasSeedOils && (
            <Chip
              startContent={<Droplets className="w-4 h-4 text-amber-500 dark:text-amber-400" />}
              color="warning"
              variant="flat"
              classNames={{
                base: "bg-warning-50 dark:bg-warning-900/20 text-warning-700 dark:text-warning-300",
                content: "font-medium"
              }}
            >
              Contains Seed Oils
            </Chip>
          )}
          
          {/* Location */}
          {locationName && (
            <Chip
              startContent={<MapPin className="w-4 h-4 text-default-500 dark:text-default-400" />}
              variant="flat"
              classNames={{
                base: "bg-default-100 dark:bg-default-50/10 text-default-700 dark:text-default-300",
                content: "font-medium"
              }}
            >
              {locationName}
            </Chip>
          )}
        </div>

        {/* Ingredients */}
        {Array.isArray(meal.ingredients) && meal?.ingredients?.length > 0 && (
          <div className="mt-2">
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-2 text-default-700 dark:text-default-300">
              <Tags className="w-4 h-4 text-default-500 dark:text-default-400" />
              Ingredients
            </h4>
            <div className="flex flex-wrap gap-2">
              {Array.isArray(meal.ingredients) && meal?.ingredients?.map((ingredient, index) => (
                <Chip
                  key={index}
                  variant="flat"
                  size="sm"
                  color="default"
                  classNames={{
                    base: "bg-default-100 dark:bg-default-50/10 text-default-700 dark:text-default-300 border border-divider",
                    content: "text-xs font-medium"
                  }}
                >
                  {ingredient}
                </Chip>
              ))}
            </div>
          </div>
        )}

        {/* Brands */}
        {Array.isArray(meal.brands) && meal?.brands?.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-2 text-default-700 dark:text-default-300">
              <ShoppingBag className="w-4 h-4 text-default-500 dark:text-default-400" />
              Brands/Sourcing
            </h4>
            <div className="flex flex-wrap gap-2">
              {Array.isArray(meal.brands) && meal?.brands?.map((brand, index) => (
                <Chip
                  key={index}
                  variant="flat"
                  size="sm"
                  color="default"
                  classNames={{
                    base: "bg-default-100 dark:bg-default-50/10 text-default-700 dark:text-default-300 border border-divider",
                    content: "text-xs font-medium"
                  }}
                >
                  {brand}
                </Chip>
              ))}
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}